import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatINR, formatApiError } from "../lib/api";
import { CheckCircle2, Circle, PackageCheck, Bike, Home, MessageCircle } from "lucide-react";
import { UpiPayCard } from "../components/UpiPayCard";
import { toast } from "sonner";
import { ProductImage } from "../components/ProductImage";

const STEPS = [
  { key: "placed", label: "Order Placed", icon: CheckCircle2 },
  { key: "packed", label: "Packed", icon: PackageCheck },
  { key: "out_for_delivery", label: "Out for Delivery", icon: Bike },
  { key: "delivered", label: "Delivered", icon: Home },
];

export default function OrderTracking() {
  const { id } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [info, setInfo] = useState({ upi: "", name: "Quick Door Now", phone: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/orders/${id}`).then((r) => setO(r.data));

  const downloadInvoice = async (oid, orderNo) => {
    try {
      const res = await api.get(`/orders/${oid}/invoice`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `QDN-INV-${orderNo || oid.slice(0, 8).toUpperCase()}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => {
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }, 300);
    } catch {
      toast.error("Could not open the invoice. Please try again.");
    }
  };

  useEffect(() => {
    api.get("/business-info").then((r) => setInfo(r.data));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cancelOrder = async () => {
    if (!window.confirm("Cancel this order?")) return;
    setBusy(true);
    try {
      await api.patch(`/orders/${id}/cancel`, { reason: "Cancelled by customer" });
      toast.success("Order cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!o)
    return (
      <div className="grid min-h-[60vh] place-items-center text-stone-500">Loading order...</div>
    );

  const activeIdx = STEPS.findIndex((s) => s.key === o.status);
  const isCancelled = o.status === "cancelled";
  const cancelReason =
    o.cancel_reason ||
    (o.status_history || []).slice().reverse().find((h) => h.status === "cancelled")?.reason ||
    "";

  return (
    <main className="mx-auto max-w-4xl px-4 pb-28 pt-8 md:px-6 md:pb-12">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs text-stone-500 font-mono">
            {o.order_no || `#${o.id.slice(0, 8).toUpperCase()}`}
          </p>
          <h1 className="font-serif text-4xl tracking-tight">
            {isCancelled ? "Order cancelled" : "Tracking your order"}
          </h1>
        </div>
        {isCancelled ? (
          <div className="flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
            Cancelled
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold text-green-800">
            <span className="pulse-dot" /> Live
          </div>
        )}
      </div>

      {isCancelled && (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-semibold">This order was cancelled.</p>
          {cancelReason && <p className="mt-1 text-xs">Reason: {cancelReason}</p>}
          {o.payment_method === "UPI" && (
            <p className="mt-1 text-xs">
              If you already paid, our team will refund you within 3-5 business days. Contact support for updates.
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="tracking-invoice-btn"
          onClick={() => downloadInvoice(o.id, o.order_no)}
          className="rounded-full bg-stone-900 px-4 py-2 text-xs font-semibold text-white hover:bg-stone-800"
        >
          Download invoice
        </button>
        {(o.status === "placed" || o.status === "packed") && (
          <button
            data-testid="tracking-cancel-btn"
            disabled={busy}
            onClick={cancelOrder}
            className="rounded-full border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            Cancel this order
          </button>
        )}
        <a
          href={`https://wa.me/91${(info.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Hi Quick Door Now, I need help with order #${o.id.slice(0, 8).toUpperCase()}`)}`}
          target="_blank"
          rel="noreferrer"
          data-testid="tracking-support-whatsapp"
          className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-4 py-2 text-xs font-semibold text-stone-800 hover:bg-stone-50"
        >
          <MessageCircle size={13} /> Support on WhatsApp
        </a>
      </div>

      {/* TIMELINE (hide when cancelled) */}
      {!isCancelled && (
      <section className="mt-6 rounded-2xl border border-stone-200 bg-white p-6">
        <div className="relative grid grid-cols-4 gap-2">
          <div className="absolute left-6 right-6 top-4 h-0.5 bg-stone-200" />
          <div
            className="absolute left-6 top-4 h-0.5 bg-orange-600 transition-all duration-500"
            style={{ width: `calc(${(Math.max(activeIdx, 0) / (STEPS.length - 1)) * 100}% - 24px)` }}
          />
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i <= activeIdx;
            return (
              <div
                key={s.key}
                data-testid={`track-step-${s.key}`}
                data-active={done ? "true" : "false"}
                className="relative flex flex-col items-center text-center"
              >
                <div
                  className={`grid h-8 w-8 place-items-center rounded-full border-2 ${
                    done
                      ? "border-orange-600 bg-orange-600 text-white"
                      : "border-stone-300 bg-white text-stone-400"
                  }`}
                >
                  {done ? <Icon size={16} /> : <Circle size={16} />}
                </div>
                <p className={`mt-2 text-[11px] font-semibold ${done ? "text-stone-900" : "text-stone-400"}`}>
                  {s.label}
                </p>
              </div>
            );
          })}
        </div>
      </section>
      )}

      {/* UPI QR reminder if not yet delivered and payment_method=UPI */}
      {o.payment_method === "UPI" && o.status !== "delivered" && o.status !== "cancelled" && (
        <section className="mt-4">
          <UpiPayCard
            upi={info.upi}
            name={info.name || "Quick Door Now"}
            amount={o.total}
            ref={o.id.slice(0, 8)}
            testid="tracking-upi"
          />
        </section>
      )}

      {/* MAP / LIVE */}
      {o.status === "out_for_delivery" && (
        <section className="mt-4 overflow-hidden rounded-2xl border border-stone-200 bg-white">
          <div className="relative h-52 bg-[url('https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1200')] bg-cover bg-center">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/80" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-600 p-3 shadow-2xl animate-bounce">
              <Bike size={20} className="text-white" />
            </div>
          </div>
          <div className="p-4 text-sm">
            <p className="font-semibold">
              {o.delivery_boy?.name || "Delivery partner"} is on the way
            </p>
            <p className="text-stone-500">
              ETA: {o.eta_label || "Will be updated shortly"} ·{" "}
              {o.delivery_boy?.phone && (
                <a href={`tel:${o.delivery_boy.phone}`} className="text-stone-800 hover:underline">
                  Call {o.delivery_boy.phone}
                </a>
              )}
            </p>
          </div>
        </section>
      )}

      {/* DETAILS */}
      <section className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <h3 className="font-display font-semibold">Items</h3>
          <div className="mt-3 space-y-2">
            {o.items.map((i, k) => (
              <div key={`${i.product_id}-${k}`} className="flex items-center gap-3 text-sm">
                <ProductImage
                  src={i.image}
                  alt={i.name}
                  className="h-10 w-10 rounded-lg object-cover"
                  testId={`tracking-product-image-${o.id}-${k}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="truncate">{i.name}</p>
                  <p className="text-xs text-stone-500">
                    {i.variant_label ? `${i.variant_label} · ` : ""}Qty {i.qty}
                  </p>
                </div>
                <p>{formatINR(i.price * i.qty)}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-stone-200 pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-stone-600">
              <span>Subtotal</span> <span>{formatINR(o.subtotal)}</span>
            </div>
            <div className="flex justify-between text-stone-600">
              <span>Delivery</span>{" "}
              <span>{o.delivery_fee === 0 ? "FREE" : formatINR(o.delivery_fee)}</span>
            </div>
            {Number(o.tip) > 0 && (
              <div className="flex justify-between text-stone-600" data-testid="tracking-tip">
                <span>Rider tip</span> <span>{formatINR(Number(o.tip))}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-stone-200 pt-2 text-base font-semibold">
              <span>Total</span> <span>{formatINR(o.total)}</span>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <h3 className="font-display font-semibold">Delivery Details</h3>
          <p className="mt-2 text-sm text-stone-700">{o.address}</p>
          <p className="text-sm text-stone-500">📞 {o.phone}</p>
          <p className="mt-2 text-xs text-stone-500">
            Payment: <b>{o.payment_method}</b>
          </p>
          <p className="mt-1 text-xs text-stone-500" data-testid="tracking-payment-status">
            Payment status: <b>{String(o.payment_status || "pending").toUpperCase()}</b>
          </p>
          <p className="mt-1 text-xs text-stone-500" data-testid="tracking-delivery-eta">
            Delivery ETA: <b>{o.eta_label || "Will be updated shortly"}</b>
          </p>
          {o.notes && (
            <p className="mt-2 rounded-lg bg-stone-50 p-2 text-xs text-stone-600">
              Note: {o.notes}
            </p>
          )}
        </div>
      </section>

      {Array.isArray(o.allocations) && o.allocations.length > 1 && (
        <section className="mt-4 rounded-2xl border border-stone-200 bg-white p-5" data-testid="tracking-suborders">
          <h3 className="font-display font-semibold">Store progress</h3>
          <p className="text-xs text-stone-500">Your order is fulfilled by {o.allocations.length} stores.</p>
          <div className="mt-3 space-y-2">
            {o.allocations.map((a, k) => {
              const done = ["packed", "out_for_delivery", "delivered"].includes(a.status);
              return (
                <div key={a.sub_id || k} className="flex items-center justify-between rounded-xl border border-stone-100 bg-stone-50 p-3">
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-stone-500">{a.sub_id || `Part ${k + 1}`} · {a.label || "Items"}</p>
                    <p className="truncate text-sm font-semibold text-stone-900">
                      {(a.items || []).map((i) => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ""}`).join(", ") || "Items"}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${
                    done ? "bg-green-50 text-green-800" : "bg-orange-50 text-orange-700"
                  }`}>
                    {(a.status || "placed").replace(/_/g, " ")}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}
